import { fetchQuery, graphql } from 'react-relay';

import { DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES } from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import logger from 'src/util/logger';

/**
 * GraphQL query for fetching property users on demand.
 * This query is only executed when the user clicks the DataPack download button,
 * rather than on initial page load.
 */
const propertyUsersQuery = graphql`
  query propertyUsersQueryQuery($uuid: ID!) {
    property(uuid: $uuid) {
      propertyUsers(first: 500) {
        edges {
          node {
            uuid
            externalIdentifier
            active { start finish }
            user {
              uuid
              externalIdentifier
              email
              givenName
              familyName
              active { start finish }
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetches property users data on demand (lazy loading).
 * Used when generating the DataPack JSON download to avoid fetching
 * user data on every page load.
 * @param {object} environment - Relay environment
 * @param {string} propertyUuid - Property UUID
 * @returns {Promise<object>} - Promise resolving to propertyUsers connection object
 */
export const fetchPropertyUsers = (environment, propertyUuid) => {
  if (!environment || !propertyUuid) {
    return Promise.reject(new Error('Missing environment or propertyUuid'));
  }

  return queryWithRetry({
    queryFn: () => {
      let resolvePromise;
      let rejectPromise;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const subscription = fetchQuery(
        environment,
        propertyUsersQuery,
        { uuid: propertyUuid },
      ).subscribe({
        next: (data) => {
          logger.log('Fetched property users for DataPack download');
          resolvePromise(data?.property?.propertyUsers);
        },
        error: (error) => {
          rejectPromise(error);
        },
      });

      // Attach unsubscribe method to the promise so queryWithRetry can cancel on timeout
      promise.unsubscribe = () => subscription.unsubscribe();

      return promise;
    },
    timeoutMs: DEFAULT_FETCH_QUERY_TIMEOUT,
    operationName: `Fetching property users for ${propertyUuid}`,
    maxRetries: DEFAULT_MAX_RETRIES,
  });
};

export default propertyUsersQuery;
